import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { Button, Modal, Popconfirm, Progress, Spin, Table } from "antd";
import Axios from "axios";
import cogoToast from "cogo-toast";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";

const token = localStorage.getItem("token_mg_WB");
const api = process.env.REACT_APP_API_URL;

const getPercent = (job) => {
  if (!job || job.total === 0) return 0;
  return Math.round(((job.sent + job.failed) / job.total) * 100);
};

const TableClass = (props) => {
  const { withSMS, setClassId, setEndVisible, refreshKey } = props;
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [notifyingJobs, setNotifyingJobs] = useState({});
  const [hostLinkLoading, setHostLinkLoading] = useState({});
  const [schedulingJobs, setSchedulingJobs] = useState({});
  // { class_id, sms, alreadyNotified, pending } — set when backend asks for confirmation
  const [confirmState, setConfirmState] = useState(null);
  const pollingRefs = useRef({});
  const schedulingPollingRefs = useRef({});

  const loadClasses = () => {
    Axios.get(`${api}/class`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      const { error, classes: data } = response.data;
      if (error) {
        cogoToast.error("Error loading classes", { position: "top-right" });
      } else {
        setClasses(data);
        setLoading(false);
      }
    });
  };

  const stopPolling = (class_id) => {
    if (pollingRefs.current[class_id]) {
      clearInterval(pollingRefs.current[class_id]);
      delete pollingRefs.current[class_id];
    }
    setNotifyingJobs((prev) => {
      const next = { ...prev };
      delete next[class_id];
      return next;
    });
  };

  const startPolling = (class_id) => {
    if (pollingRefs.current[class_id]) return;

    const id = setInterval(async () => {
      try {
        const res = await Axios.get(`${api}/class/notify/status`, {
          params: { class_id },
          headers: { Authorization: `Bearer ${token}` },
        });
        const { notificationJob, isRunning } = res.data;
        setNotifyingJobs((prev) => ({ ...prev, [class_id]: notificationJob }));

        if (!isRunning) {
          stopPolling(class_id);
          if (notificationJob?.status === "completed") {
            cogoToast.success("Notifications sent successfully", { position: "top-right" });
          } else if (notificationJob?.status === "failed") {
            const detail = notificationJob.lastError
              ? `${notificationJob.failed} failed — ${notificationJob.lastError}`
              : `${notificationJob.failed} failed`;
            cogoToast.error(`Notifications finished with errors: ${detail}`, {
              position: "top-right",
            });
          }
          loadClasses();
        }
      } catch (e) {
        console.error("Notification status poll error:", e);
      }
    }, 2500);

    pollingRefs.current[class_id] = id;
  };

  const stopSchedulingPolling = (class_id) => {
    if (schedulingPollingRefs.current[class_id]) {
      clearInterval(schedulingPollingRefs.current[class_id]);
      delete schedulingPollingRefs.current[class_id];
    }
    setSchedulingJobs((prev) => {
      const next = { ...prev };
      delete next[class_id];
      return next;
    });
  };

  const startSchedulingPolling = (class_id) => {
    if (schedulingPollingRefs.current[class_id]) return;

    const id = setInterval(async () => {
      try {
        const res = await Axios.get(`${api}/class/schedule/status`, {
          params: { class_id },
          headers: { Authorization: `Bearer ${token}` },
        });
        const { schedulingJob, isRunning } = res.data;
        setSchedulingJobs((prev) => ({ ...prev, [class_id]: schedulingJob }));

        if (!isRunning) {
          stopSchedulingPolling(class_id);
          if (schedulingJob?.status === "completed") {
            cogoToast.success("Class scheduling completed", { position: "top-right" });
          } else if (schedulingJob?.status === "failed") {
            const detail = schedulingJob.lastError
              ? `${schedulingJob.failed} failed — ${schedulingJob.lastError}`
              : `${schedulingJob.failed} failed`;
            cogoToast.error(`Scheduling finished with errors: ${detail}`, { position: "top-right" });
          }
          loadClasses();
        }
      } catch (e) {
        console.error("Scheduling status poll error:", e);
      }
    }, 2500);

    schedulingPollingRefs.current[class_id] = id;
  };

  // Starts the job once mode is confirmed. Used both directly and from the modal.
  const startJobWithMode = async (class_id, sms, mode) => {
    let url = `${api}/class/notify`;
    if (!sms) url += "/noSMS";

    try {
      const res = await Axios.post(url, null, {
        params: { class_id, mode },
        headers: { Authorization: `Bearer ${token}` },
      });
      const { error, message, status } = res.data;
      if (error) {
        cogoToast.error(message || "Error starting notifications", { position: "top-right" });
      } else {
        cogoToast.success(message, { position: "top-right" });
        setNotifyingJobs((prev) => ({ ...prev, [class_id]: status }));
        startPolling(class_id);
      }
    } catch (e) {
      if (e.response?.status === 409) {
        cogoToast.warn("Notification job already running", { position: "top-right" });
        const existing = e.response.data?.status;
        if (existing) {
          setNotifyingJobs((prev) => ({ ...prev, [class_id]: existing }));
          startPolling(class_id);
        }
      } else {
        cogoToast.error("Error starting notifications", { position: "top-right" });
      }
    }
  };

  // Initial POST without a mode — backend may respond asking to confirm.
  const sendN = async (class_id, sms = true) => {
    let url = `${api}/class/notify`;
    if (!sms) url += "/noSMS";

    try {
      const res = await Axios.post(url, null, {
        params: { class_id },
        headers: { Authorization: `Bearer ${token}` },
      });
      const { error, requiresConfirmation, alreadyNotified, pending, message, status } = res.data;

      if (error) {
        cogoToast.error(message || "Error starting notifications", { position: "top-right" });
        return;
      }
      if (requiresConfirmation) {
        setConfirmState({ class_id, sms, alreadyNotified, pending });
        return;
      }
      cogoToast.success(message, { position: "top-right" });
      setNotifyingJobs((prev) => ({ ...prev, [class_id]: status }));
      startPolling(class_id);
    } catch (e) {
      if (e.response?.status === 409) {
        cogoToast.warn("Notification job already running", { position: "top-right" });
        const existing = e.response.data?.status;
        if (existing) {
          setNotifyingJobs((prev) => ({ ...prev, [class_id]: existing }));
          startPolling(class_id);
        }
      } else {
        cogoToast.error("Error starting notifications", { position: "top-right" });
      }
    }
  };

  const endC = (class_id) => {
    setClassId(class_id);
    setEndVisible(true);
  };

  const checkC = (class_id) => {
    Axios.get(`${api}/class/check`, {
      params: { class_id },
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      const { error, message } = response.data;
      if (error)
        cogoToast.error("Error while checking class. Try again", { position: "top-right" });
      else cogoToast.success(message, { position: "top-right" });
    });
  };

  const getHostLink = (class_id) => {
    setHostLinkLoading((prev) => ({ ...prev, [class_id]: true }));
    Axios.get(`${api}/class/host-link`, {
      params: { class_id },
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        const { error, startLink, message } = response.data;
        if (error) {
          cogoToast.error(message || "Error fetching host link", { position: "top-right" });
        } else {
          navigator.clipboard.writeText(startLink);
          cogoToast.success("Host link copied. Share it with the teacher to start the session.", {
            position: "top-right",
          });
        }
      })
      .catch(() => cogoToast.error("Error fetching host link", { position: "top-right" }))
      .finally(() => setHostLinkLoading((prev) => ({ ...prev, [class_id]: false })));
  };

  const columns = [
    { title: "ID", dataIndex: "_id", key: "_id" },
    { title: "Title", dataIndex: "title", key: "title" },
    {
      title: "Course",
      dataIndex: "courses",
      key: "courses",
      render: (courses) =>
        courses.reduce((curr, next, idx) => {
          if (idx !== 0) return curr + ", " + next.title;
          return next.title;
        }, ""),
    },
    {
      title: "Batches",
      dataIndex: "batches",
      key: "batches",
      render: (batches) =>
        batches.reduce((curr, next, idx) => {
          if (idx !== 0) return curr + ", " + next.title;
          return next.title;
        }, ""),
    },
    { title: "Duration", dataIndex: "duration", key: "duration" },
    { title: "Zoom ID", dataIndex: "classID", key: "classID" },
    {
      title: "Date/Time",
      dataIndex: "dateTime",
      key: "dateTime",
      defaultSortOrder: "descend",
      render: (key) => (
        <span>{moment(key).subtract("5.5", "h").format("DD/MM/YY hh:mm:ss a")}</span>
      ),
      sorter: (a, b) => moment(a.dateTime) - moment(b.dateTime),
    },
    {
      title: "Notified",
      dataIndex: "notified",
      key: "notified",
      render: (notified, obj) => {
        const job = notifyingJobs[obj._id];
        if (job?.status === "running") return <Spin size="small" />;
        if (notified) return <CheckCircleFilled style={{ color: "#52c41a" }} />;
        return <CloseCircleFilled style={{ color: "#eb2f96" }} />;
      },
    },
    {
      title: "Send Notification",
      dataIndex: "_id",
      key: "sendNotif",
      render: (key, obj) => {
        const job = notifyingJobs[key];
        const isRunning = job?.status === "running";

        if (isRunning) {
          const processed = job.sent + job.failed;
          const label =
            job.total > 0 ? `Sending ${processed}/${job.total}...` : "Starting...";
          const skippedLabel = job.skipped > 0 ? ` (${job.skipped} skipped)` : "";
          return (
            <div style={{ minWidth: 180 }}>
              <div style={{ fontSize: 12, marginBottom: 4 }}>
                {label}
                {skippedLabel}
              </div>
              <Progress percent={getPercent(job)} size="small" />
            </div>
          );
        }

        if (!key) return null;
        return (
          <Popconfirm
            disabled={obj.ended}
            title="Are you sure?"
            onConfirm={() => (withSMS ? sendN(key) : sendN(key, false))}
          >
            <Button disabled={obj.ended} type="primary">
              Send Notification
            </Button>
          </Popconfirm>
        );
      },
    },
    {
      title: "Scheduling",
      dataIndex: "_id",
      key: "scheduling",
      render: (key, obj) => {
        const job = schedulingJobs[key] ?? obj.schedulingJob;
        if (!job) return null;
        if (job.status === "running") {
          const done = job.processed + job.failed;
          const label = job.total > 0 ? `Registering ${done}/${job.total}...` : "Starting...";
          const pct = job.total > 0 ? Math.round((done / job.total) * 100) : 0;
          return (
            <div style={{ minWidth: 180 }}>
              <div style={{ fontSize: 12, marginBottom: 4 }}>{label}</div>
              <Progress percent={pct} size="small" />
            </div>
          );
        }
        if (job.status === "completed") return <CheckCircleFilled style={{ color: "#52c41a" }} />;
        if (job.status === "failed") return <CloseCircleFilled style={{ color: "#eb2f96" }} />;
        return null;
      },
    },
    {
      title: "Check Registrants",
      dataIndex: "_id",
      key: "checkReg",
      render: (key, obj) => {
        if (key)
          return (
            <Popconfirm disabled={obj.ended} title="Are you sure?" onConfirm={() => checkC(key)}>
              <Button disabled={obj.ended}>Check Registrants</Button>
            </Popconfirm>
          );
      },
    },
    {
      title: "Host Link",
      dataIndex: "_id",
      key: "hostLink",
      render: (key, obj) => {
        if (!obj.classID) return null;
        return (
          <Button
            loading={hostLinkLoading[key]}
            onClick={() => getHostLink(key)}
          >
            Copy Host Link
          </Button>
        );
      },
    },
    {
      title: "End Class",
      dataIndex: "_id",
      key: "endClass",
      render: (key, obj) => {
        return (
          <Button onClick={() => endC(key)} disabled={obj.ended} type="danger">
            End Class
          </Button>
        );
      },
    },
  ];

  // Initial load, and reload whenever a class is created elsewhere
  useEffect(() => {
    loadClasses();
  }, [refreshKey]);

  // Auto-resume polling for any class that has a running job after initial load
  useEffect(() => {
    classes.forEach((c) => {
      if (c.notificationJob?.status === "running" && !pollingRefs.current[c._id]) {
        setNotifyingJobs((prev) => ({ ...prev, [c._id]: c.notificationJob }));
        startPolling(c._id);
      }
      if (c.schedulingJob?.status === "running" && !schedulingPollingRefs.current[c._id]) {
        setSchedulingJobs((prev) => ({ ...prev, [c._id]: c.schedulingJob }));
        startSchedulingPolling(c._id);
      }
    });
  }, [classes]);

  // Clear all intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(pollingRefs.current).forEach(clearInterval);
      Object.values(schedulingPollingRefs.current).forEach(clearInterval);
    };
  }, []);

  const totalInConfirm = confirmState
    ? confirmState.alreadyNotified + confirmState.pending
    : 0;

  return (
    <>
      {loading ? (
        <Spin style={{ display: "flex", justifyContent: "center" }} />
      ) : (
        <Table
          rowKey="_id"
          dataSource={classes}
          columns={columns}
          pagination={{
            defaultPageSize: 100,
            position: ["topRight"],
            total: classes.length,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} classes`,
          }}
        />
      )}

      <Modal
        visible={!!confirmState}
        title="Previous notifications detected"
        onCancel={() => setConfirmState(null)}
        footer={[
          <Button key="cancel" onClick={() => setConfirmState(null)}>
            Cancel
          </Button>,
          confirmState?.pending > 0 && (
            <Button
              key="new"
              onClick={() => {
                const { class_id, sms } = confirmState;
                setConfirmState(null);
                startJobWithMode(class_id, sms, "new");
              }}
            >
              New only ({confirmState?.pending})
            </Button>
          ),
          <Button
            key="all"
            type="primary"
            onClick={() => {
              const { class_id, sms } = confirmState;
              setConfirmState(null);
              startJobWithMode(class_id, sms, "all");
            }}
          >
            Re-send to all ({totalInConfirm})
          </Button>,
        ].filter(Boolean)}
      >
        {confirmState && (
          <>
            <p>
              <strong>{confirmState.alreadyNotified}</strong> of{" "}
              <strong>{totalInConfirm}</strong> enrolled users already received this
              notification.
            </p>
            {confirmState.pending > 0 ? (
              <p>
                <strong>{confirmState.pending}</strong> users have not been notified yet.
              </p>
            ) : (
              <p>All enrolled users have already been notified.</p>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default TableClass;
